import React, { useState, ReactElement, useCallback, useMemo } from 'react';
import { tw } from 'twind';

import {
  Adjustment,
  CartItem,
  GiftCardCartItem,
  useRemoveGiftCardFromCartMutation,
  GiftWithPurchasePromo,
} from '__generated__/graphql';
import { EditProductModal } from 'ui/components/EditProductModal';
import { ConfirmModal } from 'ui/components/ConfirmModal';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { useTranslate } from 'hooks/useTranslations';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { useCart } from 'hooks/useCart';
import {
  AnalyticsEvents,
  addPrefixToAnalyticProp,
  event,
  transformListProductToAnalyticsItem,
  transformVariantProductToAnalyticsProduct,
  getAnalyticsImage,
} from 'utils/analytics';
import { extractError } from 'utils/extractError';
import { useFeature } from 'hooks/useFeature';
import { ActiveCartItem } from 'types/cart';
import { useCartProductMeta } from 'hooks/useCartProductMeta';
import { getMaxQuantity } from 'utils/products';
import { ComponentType } from 'sites/types';
import {
  getGa4Item,
  removeGa4ItemFromMap,
  setGa4ItemInMap,
} from 'utils/ga4Items';
import { usePageEventsContext } from 'hooks/usePageEventsContext';
import { isNotNullish } from 'utils/isNotNullish';

import ShoppingCartItemNested from './primary';
import ShoppingCartItemSecondaryNested from './secondary';

export const ProductInfoRow: React.FC<{
  testId: string;
  name: string;
  compact?: boolean;
  info: string | ReactElement;
  className?: string;
}> = ({ name, info, compact, testId, className }) => {
  return (
    <p
      className={tw(
        'text-xs xs:text-sm flex flex-wrap break-all',
        !compact && 'md:text-base lg:text-sm xl:text-base',
        className
      )}
    >
      <span className="capitalize text-puma-black-300">{name}:&nbsp;</span>
      <span data-test-id={testId}>{info}</span>
    </p>
  );
};

type ShoppingCartItemProps = {
  compact?: boolean;
  item: ActiveCartItem | CartItem;
  basketId?: string;
  allowUpdate?: boolean;
  allowRemove?: boolean;
  preventItemLinks?: boolean;
  errorHandling?: {
    updateError?: (s: string[]) => void;
    cartUpdateError?: string[];
    cartUpdateWarnings?: string[];
  };
  updating?: boolean;
  setUpdating?: (v: boolean) => void;
  isPurchased?: boolean;
  hideQuantity?: boolean;
  minicart?: boolean;
  isFinalPrice?: boolean;
  gwpTitle?: GiftWithPurchasePromo['gwpTitle'];
};

export const ShoppingCartItem: React.FC<ShoppingCartItemProps> = ({
  compact,
  item,
  basketId,
  allowRemove,
  allowUpdate,
  minicart,
  preventItemLinks,
  errorHandling,
  updating,
  setUpdating,
  isPurchased,
  hideQuantity,
  isFinalPrice,
  gwpTitle = '',
}) => {
  const { currency } = useSiteConfig();
  const t = useTranslate();
  const personalizationEnabled = useFeature('PERSONALIZATION_ENABLED');

  const { shoppingCartItemType } = useSiteConfig();
  const { ref } = useResizeObserver();
  const { removeFromCart, updateInCart } = useCart();

  const [, removeGiftCard] = useRemoveGiftCardFromCartMutation();

  const { getMetaForProduct } = useCartProductMeta();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string>();
  const meta = getMetaForProduct(item, t('digitalGiftCard'), compact);

  const { pageviewEventHasFired } = usePageEventsContext();

  const ShoppingItemContent = useMemo(() => {
    if (shoppingCartItemType === ComponentType.SECONDARY) {
      return ShoppingCartItemSecondaryNested;
    }
    return ShoppingCartItemNested;
  }, [shoppingCartItemType]);

  const isOffline =
    item.__typename === 'ProductCartItem' ? item.product.offline : false;
  const maxOrderableQuantity =
    item.__typename === 'ProductCartItem'
      ? item.product.maxOrderableQuantity
      : 0;
  const inventoryQuantity =
    item.__typename === 'ProductCartItem' ? item.product.inventory : 0;
  const itemQuantity =
    item.__typename === 'ProductCartItem' ? item.quantity : 0;

  const maxQuantity = getMaxQuantity({
    maxOrderableQuantity,
    inventoryQuantity,
  });

  const totalInCartPrice =
    item.__typename === 'ProductCartItem'
      ? item.inCartPrice * itemQuantity
      : undefined;

  const totalProductPrice =
    item.__typename === 'ProductCartItem' && item.product.productPrice?.price
      ? item.product.productPrice?.price * itemQuantity
      : (item as GiftCardCartItem).amount;

  const salePrice =
    item.__typename === 'ProductCartItem' &&
    item.product.productPrice?.salePrice
      ? item.product.productPrice?.salePrice * itemQuantity
      : undefined;

  const promotionPrice =
    item.__typename === 'ProductCartItem' &&
    item.product.productPrice?.promotionPrice
      ? item.product.productPrice?.promotionPrice * itemQuantity
      : totalInCartPrice;

  const purchasedPrice =
    item.__typename === 'ProductCartItem' && item.purchasedPrice?.price
      ? item.purchasedPrice?.price * itemQuantity
      : (item as GiftCardCartItem).amount;

  const purchasedPromotionPrice =
    item.__typename === 'ProductCartItem' && item.purchasedPrice?.promotionPrice
      ? item.purchasedPrice?.promotionPrice * itemQuantity
      : undefined;

  const personalizationTotalPrice =
    item.__typename === 'ProductCartItem' &&
    isNotNullish(item.personalizationPrice)
      ? parseFloat(item.personalizationPrice) * itemQuantity
      : undefined;

  const onQuantityChange = useCallback(
    async (newQty: string | number) => {
      if (!basketId || !setUpdating || item.__typename !== 'ProductCartItem') {
        return;
      }

      if (errorHandling?.updateError) {
        setError(undefined);
        const errors = (errorHandling?.cartUpdateError ?? []).filter(
          error => error !== item.product.id
        );
        errorHandling?.updateError(errors);
      }

      setUpdating(true);

      const oldQuantity = item.quantity;
      const newQuantity = Number.parseInt(newQty as string);
      const quantityDiff = newQuantity - oldQuantity;
      const newValue =
        Math.round(
          item.inCartPrice *
            (quantityDiff > 0 ? quantityDiff : oldQuantity - newQuantity) *
            100
        ) / 100;
      const res = await updateInCart({
        itemId: item.id,
        quantity: newQuantity,
        productId: item.product.id,
        masterId: item.product.masterId,
      });
      if (res.data?.updateItemInCart) {
        const ga4Item = getGa4Item(item.product?.styleNumber);

        const analyticsItem = transformListProductToAnalyticsItem({
          product: item.product,
          currency: currency.code,
          quantity: quantityDiff > 0 ? quantityDiff : oldQuantity - newQuantity,
          categories: {
            item_category: item.product.primaryCategoryId,
          },
          itemListId: ga4Item?.lid,
          itemListName: ga4Item?.lname,
          index: ga4Item?.idx,
          creativeName: ga4Item?.cname,
          creativeSlot: ga4Item?.cslot,
          promotionId: ga4Item?.pid,
          promotionName: ga4Item?.pname,
          size: item.product?.size.label,
        });

        const analyticsImage = getAnalyticsImage(
          meta.imageUrl,
          item.product.images[0].href
        );

        const ecommerceData = {
          ecommerce: {
            item_list_id: ga4Item?.lid,
            item_list_name: ga4Item?.lname,
            currency: currency.code,
            value: newValue,
            image_type: analyticsImage.imageType,
            image_number:
              analyticsImage.imageNumber !== 'unavailable'
                ? addPrefixToAnalyticProp(analyticsImage.imageNumber)
                : undefined,
            items: [analyticsItem],
          },
        };

        if (quantityDiff > 0) {
          event(AnalyticsEvents.GA4EC_AddItemToCart, {
            event_name: AnalyticsEvents.ADD_ITEM_LIST_TO_CART,
            ...ecommerceData,
          });
        } else {
          event(AnalyticsEvents.GA4EC_RemoveItemFromCart, {
            event_name: AnalyticsEvents.REMOVE_ITEM_FROM_CART,
            ...ecommerceData,
          });
        }

        if (pageviewEventHasFired) {
          event(AnalyticsEvents.GA4_CustomEvent, {
            event_name: AnalyticsEvents.QUANTITY_CHANGE,
            event_params: {
              item_id_ep: analyticsItem.item_id,
              item_name_ep: analyticsItem.item_name,
            },
          });
        }
      }

      const error = extractError(res);
      if (error) {
        if (errorHandling?.updateError && errorHandling?.cartUpdateError) {
          errorHandling.updateError(
            errorHandling.cartUpdateError?.concat(item.product.id)
          );
          setError(error);
        }
      }

      setUpdating(false);
    },
    [
      basketId,
      item,
      updateInCart,
      setUpdating,
      errorHandling,
      currency.code,
      meta.imageUrl,
      pageviewEventHasFired,
    ]
  );

  const handleClickEdit = () => setEditing(true);

  const handleClickRemove = () => setRemoving(true);

  const showAlertMessage = Boolean(meta.adjustQuantityMessage || error);

  const promotionMessages =
    item.__typename === 'ProductCartItem' && item.hasStock
      ? item.adjustments
          .map((adjustment: Adjustment) => adjustment.promotion?.calloutMessage)
          .filter((item): item is string => Boolean(item))
      : [];

  return (
    <>
      <div className="w-full" ref={ref}>
        <ShoppingItemContent
          item={item}
          compact={compact}
          preventItemLinks={preventItemLinks}
          isOffline={isOffline}
          meta={meta}
          updating={updating}
          removing={removing}
          maxPurchasableQuantity={maxQuantity}
          showAlertMessage={showAlertMessage}
          allowUpdate={!!allowUpdate}
          allowRemove={!!allowRemove}
          minicart={!!minicart}
          hideQuantity={!!hideQuantity}
          onChangeQuantity={onQuantityChange}
          basketId={basketId}
          totalProductPrice={isPurchased ? purchasedPrice : totalProductPrice}
          salePrice={isPurchased ? undefined : salePrice}
          promotionMessages={promotionMessages}
          promotionPrice={
            isPurchased ? purchasedPromotionPrice : promotionPrice
          }
          gwpTitle={gwpTitle}
          personalizationPrice={personalizationTotalPrice}
          onEdit={handleClickEdit}
          onRemove={handleClickRemove}
          error={error}
          isPurchased={isPurchased}
          isFinalPrice={isFinalPrice}
        />
      </div>
      {item.__typename === 'ProductCartItem' && allowUpdate && editing && (
        <EditProductModal
          price={item.inCartPrice}
          size={item.product.size.value}
          color={item.product.colorValue}
          id={item.product.id}
          masterId={item.product.masterId}
          onClose={() => setEditing(false)}
          update={async ({ size, swatch }) => {
            const productPersonalization = personalizationEnabled
              ? {
                  imageURL: item.personalizationImageURL,
                  largeImageURL: item.personalizationLargeImageURL,
                  nameText: item.personalizationName,
                  numberText: item.personalizationNumber,
                  price: item.personalizationPrice,
                  recipeID: item.personalizationRecipeID,
                }
              : null;
            const updateRes = await updateInCart({
              itemId: item.id,
              productId: item.product.masterId,
              quantity: item.quantity,
              swatch,
              size,
              productPersonalization: { ...productPersonalization },
              masterId: item.product.masterId,
            });

            if (updateRes.data?.updateItemInCart) {
              const oldKey = `${item.product.masterId}_${item.product.colorValue}`;
              const ga4Item = getGa4Item(oldKey);
              if (ga4Item) {
                const updatedGa4Item = { ...ga4Item, cart: '1' };
                removeGa4ItemFromMap(oldKey);
                setGa4ItemInMap(
                  `${item.product.masterId}_${swatch}`,
                  updatedGa4Item
                );
              }
            }

            return updateRes;
          }}
        />
      )}
      {removing && (
        <ConfirmModal
          loading={loading}
          onClickOutside={() => setRemoving(false)}
          onConfirm={() => {
            if (basketId) {
              setLoading(true);
              if (item.__typename === 'ProductCartItem') {
                removeFromCart({
                  itemId: item.id,
                }).then(() => {
                  event(AnalyticsEvents.REMOVE_FROM_CART, {
                    ecommerce: {
                      currencyCode: currency.code,
                      remove: {
                        products: [
                          {
                            ...transformVariantProductToAnalyticsProduct(
                              item.product,
                              true
                            ),
                            quantity: item.quantity,
                          },
                        ],
                      },
                    },
                  });

                  const ga4Item = getGa4Item(item.product?.styleNumber);
                  if (ga4Item) {
                    const modifiedGa4Item = { ...ga4Item, cart: undefined };
                    setGa4ItemInMap(
                      item.product?.styleNumber,
                      modifiedGa4Item,
                      true
                    );
                  }

                  const analyticsItem = transformListProductToAnalyticsItem({
                    product: item.product,
                    currency: currency.code,
                    quantity: item.quantity,
                    categories: {
                      item_category: item.product.primaryCategoryId,
                    },
                    itemListId: ga4Item?.lid,
                    itemListName: ga4Item?.lname,
                    index: ga4Item?.idx,
                    creativeName: ga4Item?.cname,
                    creativeSlot: ga4Item?.cslot,
                    promotionId: ga4Item?.pid,
                    promotionName: ga4Item?.pname,
                    size: item.product.size.label,
                  });

                  const analyticsImage = getAnalyticsImage(
                    meta.imageUrl,
                    item.product.images[0].href
                  );

                  const newValue = item.inCartPrice * item.quantity;

                  event(AnalyticsEvents.GA4EC_RemoveItemFromCart, {
                    event_name: AnalyticsEvents.REMOVE_ITEM_FROM_CART,
                    ecommerce: {
                      item_list_id: ga4Item?.lid,
                      item_list_name: ga4Item?.lname,
                      currency: currency.code,
                      value: newValue,
                      image_type: analyticsImage.imageType,
                      image_number:
                        analyticsImage.imageNumber !== 'unavailable'
                          ? addPrefixToAnalyticProp(analyticsImage.imageNumber)
                          : undefined,
                      items: [analyticsItem],
                    },
                  });

                  setRemoving(false);
                  setLoading(false);
                });
              } else {
                removeGiftCard({
                  input: {
                    basketId,
                    giftCardId: item.id,
                  },
                }).then(() => {
                  setRemoving(false);
                  setLoading(false);
                });
              }
            }
          }}
          dataTestId="cart-remove-item-modal"
        >
          <h1 className="text-xl pr-8">{t('removeItemConfirmation')}</h1>
          <ShoppingCartItem
            item={item}
            gwpTitle={gwpTitle}
            compact
            minicart
            hideQuantity
          />
        </ConfirmModal>
      )}
    </>
  );
};
